import { useState, useEffect } from 'react';
import { Button, Card, Container, Center, Stack, Spinner, Input } from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useController } from 'react-hook-form';
import { z } from 'zod';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import TextEditor from '@/components/ui/text-editor';
import GroupBlock from './elements/group-block';
import api from '@/utils/api';
import useCollectionTypeStore from '@/utils/use-collection-type';
import { Collection, ContentType, CollectionType, GroupForm, Content } from '@/lib/types';

interface Type extends CollectionType {
  content_type: ContentType;
}

interface CollectionContent {
  groups: GroupForm[];
}

interface CollectionWithContent extends Collection {
  content: CollectionContent;
}

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  content: z
    .array(
      z.object({
        name: z.string().min(1, 'Group name is required'),
        contents: z.array(z.string()),
      }),
    )
    .optional(),
});

type FormData = z.infer<typeof formSchema>;

export default function EditCollection() {
  const { collectionTypes } = useCollectionTypeStore();

  const [collectionType, setCollectionType] = useState<CollectionType>();
  const [collection, setCollection] = useState<Collection | null>(null);
  const [contentTypes, setContentTypes] = useState<ContentType[]>([]);
  const [contents, setContents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { workspaceId, collectionTypeId, id } = useParams<{
    workspaceId: string;
    collectionTypeId: string;
    id: string;
  }>();

  const navigate = useNavigate();

  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const description = useController({
    control,
    name: 'description',
    defaultValue: '',
  });

  const content = useController({
    control,
    name: 'content',
    defaultValue: [],
  });

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!collectionTypeId) return;

      setIsLoading(true);

      try {
        const [collectionTypeData, contentTypesData, collectionData] = await Promise.all([
          Promise.resolve(
            collectionTypes.find((type) => type.id === parseInt(collectionTypeId, 10)),
          ),
          api.get<Type[]>(`collection-content-types/collection-types/${collectionTypeId}`),
          id ? api.get<CollectionWithContent>(`collections/${id}`) : Promise.resolve(null),
        ]);

        if (!isMounted) return;

        if (collectionTypeData) {
          setCollectionType(collectionTypeData);
        }

        if (contentTypesData.data) {
          const types = contentTypesData.data.map((item) => item.content_type);
          setContentTypes(types);
        }

        if (collectionData && collectionData.data) {
          const data = collectionData.data;

          const collectionRes = await api.get(`collections/reference/${data.reference_id}`);

          if (isMounted && collectionRes.data) {
            const contents: any[] = [];
            collectionRes.data.ContentWithStructure.groups.forEach((content: any) =>
              contents.push(...content.ContentValues),
            );
            setContents(contents);
          }

          if (isMounted) {
            setCollection(data);

            let contentGroups: GroupForm[] = [];
            if (data.content && data.content.groups) {
              contentGroups = data.content.groups;
            }

            reset({
              name: data.name,
              description: data.description || '',
              content: contentGroups,
            });
          }
        }
      } catch (err: any) {
        if (isMounted) {
          toaster.create({
            type: 'error',
            title: err.response?.data?.error || 'Failed to fetch data',
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [collectionTypeId, collectionTypes, workspaceId, id, reset]);

  const onSubmit = async (data: FormData) => {
    if (!collectionTypeId) {
      return;
    }

    try {
      const payload = {
        name: data.name,
        description: data.description,
        collection_type_id: parseInt(collectionTypeId),
        content: {
          groups: data.content || [],
        },
        status: collection?.status,
      };

      await api.put(`collections/${id}`, payload);

      toaster.create({
        type: 'success',
        title: 'Collection updated successfully',
      });

      navigate(`/${workspaceId}/collection-types/${collectionTypeId}/collections/my-collections`);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to update collection',
      });
    }
  };

  const applyChanges = (groups: GroupForm[], item: Content) => {
    content.field.onChange(groups);
    if (item) {
      setContents((prev) => [...prev, item]);
    }
  };

  return (
    <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
      <Card.Header textAlign={'center'}>
        <Card.Title fontSize='3xl'>Edit {collectionType?.name || 'Content'}</Card.Title>
      </Card.Header>
      <Card.Body>
        {isLoading && (
          <Center h='300px'>
            <Spinner size='xl' />
          </Center>
        )}
        <Container maxW={'3xl'}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={4}>
              <Field label='Name' invalid={!!errors.name} errorText={errors.name?.message}>
                <Input {...register('name')} />
              </Field>
              <Field label='Description'>
                <TextEditor
                  initialValue={description.field.value}
                  onChange={(newContent: string) => {
                    description.field.onChange(newContent);
                  }}
                />
              </Field>

              {workspaceId && (
                <GroupBlock
                  groups={content.field.value || []}
                  errors={errors.content}
                  contentTypes={contentTypes}
                  workspaceId={parseInt(workspaceId, 10)}
                  contents={contents || []}
                  onChange={applyChanges}
                />
              )}

              <Button
                colorPalette='brand'
                type={'submit'}
                width={'full'}
                mt={5}
                loading={isSubmitting}
                loadingText='Loading...'
              >
                Save
              </Button>
            </Stack>
          </form>
        </Container>
      </Card.Body>
    </Card.Root>
  );
}
